// src/components/energy-reset/XpRing.tsx
import { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  xp: number;
  target: number;
  size?: number;      // диаметр, px
  stroke?: number;    // толщина кольца, px
  className?: string;
};

export default function XpRing({
  xp,
  target,
  size = 140,
  stroke = 10,
  className = "",
}: Props) {
  // предыдущие значения (для определения "левел-апа")
  const prevXpRef = useRef(xp);
  const prevTargetRef = useRef(target);

  // то, по чему реально рисуем кольцо (визуальная цель/XP)
  const [visualTarget, setVisualTarget] = useState(target);
  const [visualXp, setVisualXp] = useState(xp);

  // что пишем в подписи "Цель: N" (делаем маленький flip-аним при росте)
  const [displayTarget, setDisplayTarget] = useState(target);

  // флаг вспышки при "левел-апе"
  const [levelUp, setLevelUp] = useState(false);

  // вычисления геометрии кольца
  const radius = useMemo(() => size / 2 - stroke / 2, [size, stroke]);
  const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);

  // прогресс по визуальным значениям (0..1)
  const pct = useMemo(() => {
    if (visualTarget <= 0) return 0;
    return Math.max(0, Math.min(visualXp / visualTarget, 1));
  }, [visualXp, visualTarget]);

  const dashOffset = useMemo(
    () => circumference * (1 - pct),
    [circumference, pct]
  );

  // Основная логика анимации "подъёма цели"
  useEffect(() => {
    const prevXp = prevXpRef.current;
    const prevTarget = prevTargetRef.current;

    // событие «достигли прошлой цели» (левел-ап)
    const justLeveled = prevXp < prevTarget && xp >= prevTarget;

    if (justLeveled) {
      // Фаза 1: дорисовать старую цель до 100% и вспыхнуть
      setLevelUp(true);
      setVisualTarget(prevTarget); // рисуемся относительно старой цели
      setVisualXp(xp);             // текущий XP (может быть > старой цели)
      setDisplayTarget(prevTarget); // в подписи пока старая цель

      const t1 = setTimeout(() => {
        // Фаза 2: «поднять» цель и плавно перейти к новому проценту
        setVisualTarget(target);
        setDisplayTarget(target);
        setLevelUp(false);
      }, 650); // тайминг вспышки/дорисовки

      prevXpRef.current = xp;
      prevTargetRef.current = target;
      return () => clearTimeout(t1);
    }

    // обычный случай: просто обновляем визуальные значения
    setVisualXp(xp);
    setVisualTarget(target);
    setDisplayTarget(target);

    prevXpRef.current = xp;
    prevTargetRef.current = target;
  }, [xp, target]);

  // визуальная подпись значения в центре
  const centerLabel = useMemo(() => {
    return `${xp}`;
  }, [xp]);

  // для SVG
  const center = size / 2;

  return (
    <div className={`relative rounded-2xl bg-white border border-gray-200 shadow-sm p-4 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">Твои искорки</div>
        <div className="text-xs text-gray-500">прогресс по цели</div>
      </div>

      <div className="relative mx-auto" style={{ width: size, height: size }}>
        {/* подложка для вспышки при level-up */}
        {levelUp && (
          <span
            className="pointer-events-none absolute inset-0 rounded-full animate-ping"
            style={{
              boxShadow: "0 0 0 2px rgba(16,185,129,0.35)",
            }}
          />
        )}

        <svg
          width={size}
          height={size}
          className="block"
          viewBox={`0 0 ${size} ${size}`}
        >
          {/* Трек */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(0,0,0,0.08)"
            strokeWidth={stroke}
          />

          {/* Прогресс — с анимацией штриха */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            className="text-emerald-500"
            style={{
              transform: "rotate(-90deg)",
              transformOrigin: "50% 50%",
              strokeDasharray: circumference,
              strokeDashoffset: dashOffset,
              transition: "stroke-dashoffset 500ms ease",
              filter: levelUp ? "drop-shadow(0 0 8px rgba(16,185,129,0.6))" : "none",
            }}
          />
        </svg>

        {/* Центр — числа и подписи */}
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center leading-tight">
            <div className="text-2xl font-bold">{centerLabel}</div>
            <div className="text-[11px] text-gray-500">искорки</div>
          </div>
        </div>
      </div>

      {/* Нижняя подпись: «Цель: N» с лёгким flip при смене цели */}
      <div className="mt-3 flex items-center justify-center gap-2">
        <span className="text-xs text-gray-500">Цель:</span>
        <span
          key={displayTarget} // ключ заставляет сделать небольшой flip
          className="text-sm font-semibold inline-block transition-transform duration-300 ease-out"
          style={{ transform: "translateY(0)" }}
        >
          {displayTarget}
        </span>

        {/* маленькая стрелка «вверх» во время level-up */}
        {levelUp && (
          <span className="text-emerald-600 text-sm" aria-hidden>
            ↑
          </span>
        )}
      </div>
    </div>
  );
}
